import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ButtonLeadingIcon } from '@/lib/button-leading-icon';
import { cn } from '@/lib/utils';

export const pagePrimaryActionClassName =
  'inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90';

const baseActionClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors';

type RowActionProps = {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
};

function ViewActionButton({ onClick, disabled, children, className }: RowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseActionClass,
        'text-xs sm:text-sm h-8 px-3 border border-blue-500/50 text-blue-600 hover:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20',
        className,
      )}
    >
      <ButtonLeadingIcon name="eye" size={14} />
      {children}
    </button>
  );
}

function EditActionButton({ onClick, disabled, children, className }: RowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseActionClass,
        'text-xs sm:text-sm h-8 px-3 border border-primary text-primary hover:bg-primary/10',
        className,
      )}
    >
      <ButtonLeadingIcon name="square-pen" size={14} />
      {children}
    </button>
  );
}

function DeleteActionButton({ onClick, disabled, children, className }: RowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseActionClass,
        'text-xs sm:text-sm h-8 px-3 border border-red-500 text-red-600 hover:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20',
        className,
      )}
    >
      <ButtonLeadingIcon name="delete" size={14} />
      {children}
    </button>
  );
}

function CloneActionButton({ onClick, disabled, children, className }: RowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseActionClass,
        'text-xs sm:text-sm h-8 px-3 border border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20',
        className,
      )}
    >
      <ButtonLeadingIcon name="copy" size={14} />
      {children}
    </button>
  );
}

function PasswordActionButton({ onClick, disabled, children, className }: RowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseActionClass,
        'text-xs sm:text-sm h-8 px-3 border border-amber-500/50 text-amber-600 hover:bg-amber-500/10 dark:text-amber-400 dark:hover:bg-amber-500/20',
        className,
      )}
    >
      <ButtonLeadingIcon name="lock" size={14} />
      {children}
    </button>
  );
}

function DeactivateActionButton({ onClick, disabled, children, className }: RowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseActionClass,
        'text-xs sm:text-sm h-8 px-3 border border-destructive text-destructive hover:bg-destructive/10',
        className,
      )}
    >
      <ButtonLeadingIcon name="ban" size={14} />
      {children}
    </button>
  );
}

export type AdminTableRowActionsProps = {
  act: (key: string) => string;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onClone?: () => void;
  onDeactivate?: () => void;
  onPassword?: () => void;
  cloneLabel?: ReactNode;
  deactivateLabel?: ReactNode;
  passwordLabel?: ReactNode;
  isDeleting?: boolean;
  deactivateDisabled?: boolean;
  compact?: boolean;
  extra?: ReactNode;
};

export function AdminTableRowActions({
  act,
  onView,
  onEdit,
  onDelete,
  onClone,
  onDeactivate,
  onPassword,
  cloneLabel = 'Clone',
  deactivateLabel = 'Deactivate',
  passwordLabel = 'Change Password',
  isDeleting,
  deactivateDisabled,
  compact,
  extra,
}: AdminTableRowActionsProps) {
  const sizeClass = compact ? 'text-xs h-7 px-2' : undefined;

  return (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      {onView && (
        <ViewActionButton onClick={onView} className={sizeClass}>
          {act('view')}
        </ViewActionButton>
      )}
      {onEdit && (
        <EditActionButton onClick={onEdit} className={sizeClass}>
          {act('edit')}
        </EditActionButton>
      )}
      {onPassword && (
        <PasswordActionButton onClick={onPassword} className={sizeClass}>
          {passwordLabel}
        </PasswordActionButton>
      )}
      {onClone && (
        <CloneActionButton onClick={onClone} className={sizeClass}>
          {cloneLabel}
        </CloneActionButton>
      )}
      {onDeactivate && (
        <DeactivateActionButton
          onClick={onDeactivate}
          disabled={deactivateDisabled}
          className={sizeClass}
        >
          {deactivateLabel}
        </DeactivateActionButton>
      )}
      {onDelete && (
        <DeleteActionButton onClick={onDelete} disabled={isDeleting} className={sizeClass}>
          {act('delete')}
        </DeleteActionButton>
      )}
      {extra}
    </div>
  );
}

export function PagePrimaryActionButton({
  onClick,
  children,
  className,
  disabled,
}: {
  onClick: () => void;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(pagePrimaryActionClassName, disabled && 'opacity-50', className)}
    >
      <ButtonLeadingIcon name="plus" size={16} />
      {children}
    </button>
  );
}

const viewLinkClass =
  'inline-flex items-center gap-1.5 justify-center rounded-md text-sm font-medium h-9 px-3 border border-blue-500/50 text-blue-600 hover:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20 transition-colors';
const editLinkClass =
  'inline-flex items-center gap-1.5 justify-center rounded-md text-sm font-medium h-9 px-3 border border-primary text-primary hover:bg-primary/10 transition-colors';

export function ViewActionLink({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={cn(viewLinkClass, className)}>
      <ButtonLeadingIcon name="eye" size={14} />
      {children}
    </Link>
  );
}

export function EditActionLink({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={cn(editLinkClass, className)}>
      <ButtonLeadingIcon name="square-pen" size={14} />
      {children}
    </Link>
  );
}

export function PagePrimaryActionLink({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={cn(pagePrimaryActionClassName, className)}>
      <ButtonLeadingIcon name="plus" size={16} />
      {children}
    </Link>
  );
}
